import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { USA_HUB, USA_COMMERCIAL_PAGES } from "@/data/usaPages";
import { stripLocale } from "@/lib/i18n";

/**
 * Shared template for the six /india-*-for-usa commercial pages.
 * Each page has a unique H1, meta, sections and FAQ so there is no
 * duplicate content.
 */
export const UsaCommercial = () => {
  const location = useLocation();
  const page = USA_COMMERCIAL_PAGES.find((p) => p.slug === stripLocale(location.pathname));
  const { open } = useStartProject();

  useSeo({
    title: page ? page.title : "US programme",
    description: page ? page.metaDescription : undefined,
    path: page ? page.slug : "/usa",
    type: "article",
    jsonLd: page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}${page.slug}#service`,
              name: page.h1,
              description: page.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: "United States" },
              serviceType: page.h1.split("—")[0].trim(),
              url: `${SITE_URL}${page.slug}`,
              isPartOf: {
                "@type": "WebPage",
                "@id": `${SITE_URL}${USA_HUB.slug}`,
                name: USA_HUB.h1,
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${page.slug}#faq`,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "USA", path: USA_HUB.slug },
              { name: page.h1.split("—")[0].trim(), path: page.slug },
            ]),
          ],
        }
      : null,
    jsonLdId: "usa-commercial-jsonld",
  });

  if (!page) return <Navigate to="/usa" replace />;

  const idx = USA_COMMERCIAL_PAGES.findIndex((p) => p.slug === page.slug);
  const prev =
    USA_COMMERCIAL_PAGES[
      (idx - 1 + USA_COMMERCIAL_PAGES.length) % USA_COMMERCIAL_PAGES.length
    ];
  const next =
    USA_COMMERCIAL_PAGES[(idx + 1) % USA_COMMERCIAL_PAGES.length];

  return (
    <article data-testid="usa-commercial-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal
          as="nav"
          aria-label="Breadcrumbs"
          style={{ marginBottom: "1.2rem" }}
        >
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }}>
              Home
            </Link>{" "}
            ·{" "}
            <Link to="/usa" style={{ color: "inherit" }}>
              USA
            </Link>{" "}
            · {page.h1.split("—")[0].trim()}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="usa-commercial-eyebrow">
            <span
              aria-hidden="true"
              style={{
                marginRight: ".5rem",
                fontSize: "1.4em",
                verticalAlign: "middle",
              }}
            >
              🇺🇸
            </span>
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="usa-commercial-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: ".8rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="usa-commercial-cta-primary"
          >
            Start Your Project →
          </button>
          <Link to="/usa" className="btn btn-ghost" data-testid="usa-commercial-cta-hub">
            Back to US hub
          </Link>
        </Reveal>
      </header>

      {/* BODY sections */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        {(page.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
            {s.subsections && (
              <div style={{ marginTop: "1rem" }}>
                {s.subsections.map((ss, ix) => (
                  <div
                    key={ix}
                    style={{
                      borderTop: "1px solid var(--line)",
                      paddingBlock: "1rem",
                    }}
                  >
                    <h3
                      style={{
                        fontFamily: "var(--display)",
                        fontSize: "1.05rem",
                        margin: "0 0 .3rem",
                        letterSpacing: "-.005em",
                      }}
                    >
                      {ss.h3}
                    </h3>
                    <p
                      style={{
                        color: "var(--ink-soft)",
                        fontSize: ".98rem",
                        lineHeight: 1.65,
                        margin: 0,
                        maxWidth: "68ch",
                      }}
                    >
                      {ss.body}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Reveal>
        ))}
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
          maxWidth: "820px",
        }}
        data-testid="usa-commercial-faq"
      >
        <Reveal as="p" className="eyebrow">
          Frequently asked
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Questions US buyers ask us on this programme.
        </Reveal>
        {page.faqs.map(([q, a], i) => (
          <Reveal
            key={i}
            as="details"
            data-testid={`usa-commercial-faq-${i}`}
            style={{
              borderBottom: "1px solid var(--line)",
              padding: "1.4rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                color: "var(--ink)",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                marginTop: ".8rem",
                color: "var(--ink-soft)",
                lineHeight: 1.7,
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </Reveal>
        ))}
      </section>

      {/* SISTER PROGRAMMES */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
        data-testid="usa-commercial-related"
      >
        <Reveal as="p" className="eyebrow">
          Other US programmes
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Continue on the US hub.
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1rem",
          }}
        >
          {USA_COMMERCIAL_PAGES.filter((p) => p.slug !== page.slug).map((p) => (
            <Link
              key={p.id}
              to={p.slug}
              style={{
                display: "block",
                padding: "1rem 1.2rem",
                border: "1px solid var(--line)",
                textDecoration: "none",
                color: "inherit",
              }}
              data-testid={`usa-commercial-related-${p.id}`}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".66rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                US programme
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.05rem",
                  margin: ".35rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.3,
                }}
              >
                {p.h1.split("—")[0].trim()}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <Reveal as="p" className="eyebrow">
          Start a US programme
        </Reveal>
        <Reveal as="h2" className="display" style={{ maxWidth: "34ch" }}>
          Talk to an India sourcing expert.
        </Reveal>
        <Reveal
          as="div"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="usa-commercial-cta-start-project"
          >
            Start Your Project <ArrowRight size={16} strokeWidth={1.6} aria-hidden />
          </button>
          <Link to="/contact" className="btn btn-ghost" data-testid="usa-commercial-cta-contact">
            Send your requirement
          </Link>
        </Reveal>
      </section>

      {/* Pagination */}
      <nav
        className="wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBlock: "2rem 4rem",
          borderTop: "1px solid var(--line)",
          fontFamily: "var(--mono)",
          fontSize: ".74rem",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
        aria-label="US programme pagination"
        data-testid="usa-commercial-pagination"
      >
        <Link to={prev.slug} className="btn btn-ghost">
          ← {prev.h1.split("—")[0].trim()}
        </Link>
        <Link to={next.slug} className="btn btn-ghost">
          {next.h1.split("—")[0].trim()} →
        </Link>
      </nav>
    </article>
  );
};

export default UsaCommercial;
